<script context='module'>
  export const DEFAULT = 'default';
  export const SUCCESS = 'success';
  export const WARNING = 'warning';
  export const ERROR = 'error';

  export const SMALL = 'size-small';
</script>

<script>
  import { createEventDispatcher } from 'svelte';
  import * as uuid from 'uuid';
  import Button, { NONE } from '../Button';
  import Icon from '../Icon';
  import Tooltip from '../Tooltip';
  import { slide, fade } from 'svelte/transition';
  import Motion, { useMotion } from '../Motion';
  import { onScreen as easing } from '../Motion/easing';
  import { MEDIUM, EXTRA_FAST } from '../Motion/duration';

  const motion = useMotion();
  const duration = motion === Motion.NONE ? 0 : MEDIUM;

  export let appearance = 'default';
  export let size = undefined;
  export let testid = undefined;
  export let copy = true;
  export let expanded = false;
  export let dismissable = false;
  
  let hidden = false;
  let copied = false;
  const messageId = uuid.v4();

  export function toggleExpanded() {
    expanded = !expanded;
  }

  export function setExpanded(expand) {
    expanded = expand;
  }

  const dispatch = createEventDispatcher();
  export function dismiss() {
    dispatch('dismiss');
    hidden = true;
  }

  let copyTimer;
  function copyText() {
    copied = true;
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => {
      copied = false;
    }, 2000);

    // eslint-disable-next-line unicorn/prefer-dom-node-text-content
    const messageTitle = document.querySelector(`#title-${messageId}`).innerText;
    // eslint-disable-next-line unicorn/prefer-dom-node-text-content
    const content = document.querySelector(`#content-${messageId}`).innerText;

    const copiedText = messageTitle + ($$slots.items ? `\n---\n${content}` : '');

    navigator.clipboard.writeText(copiedText);
  }
</script>

{#if !hidden}
  <div class='message {appearance} {size}' data-cy={testid}>
    <div class='message-header'>
      <div id='title-{messageId}' class='message-title {size}'>
        <slot />
      </div>
      
      <div class='actions'>
        {#if copy}
          <Tooltip let:tooltip>
            <svelte:fragment slot='tooltip'>
              {#if copied}
                Copied
              {:else}
                Copy message
              {/if}
            </svelte:fragment>
            <Button appearance={NONE} on:click={copyText} testid='copy'>
              {#if copied}
                <div class='iconContainer' in:fade={{ duration: EXTRA_FAST, easing }}>
                  <Icon icon='check_circle' {tooltip} />
                </div>
              {:else}
                <div class='iconContainer' in:fade={{ duration: EXTRA_FAST, easing }}>
                  <Icon icon='content_copy' {tooltip} />
                </div>
              {/if}
            </Button>
          </Tooltip>
        {/if}

        {#if $$slots.items}
          <span class='arrow-drop-down' class:expanded>
            <Button appearance={NONE} on:click={toggleExpanded} testid='expand'>
              <div class='iconContainer'>
                <Icon icon='arrow_drop_down' />
              </div>
            </Button>
          </span>
        {:else if dismissable}
          <Button appearance={NONE} on:click={dismiss} testid='close'>
            <div class='iconContainer'>
              <Icon icon='cancel' />
            </div>
          </Button>
        {/if}
      </div>
    </div>

    {#if expanded}
      <div class='content' transition:slide|local={{ duration, easing }}>
        <slot name='items'/>
      </div>
    {/if}
  </div>
{/if}

<!-- Content of the message regardless if its expanded or not-->
<div id='content-{messageId}' class='content hidden' aria-hidden="true">
  <slot name='items'/>
</div>

<style lang="postcss">
  .message {
    padding: var(--spacing--xs) var(--spacing--s);
    border-radius: var(--radius--m);
    color: var(--Message--color-foreground);
    background-color: var(--Message--color-background);
    flex-grow: 1;
    font-size: var(--font-size--body-sentence);


    &.size-small {
      padding: 5px var(--spacing--s);
      font-size: var(--font-size--body-table);
    }

    &.default {
      --Message--color-foreground: var(--color--blue);
      --Message--color-background: var(--color--blue-5);
      --Icon--color: var(--color--blue);
      --Icon--color--hover: var(--color--blue-70);
      --Icon--color--active: var(--color--blue-90);
    }

    &.success {
      --Message--color-foreground: var(--color--green);
      --Message--color-background: var(--color--green-5);
      --Icon--color: var(--color--green);
      --Icon--color--hover: var(--color--green-70);
      --Icon--color--active: var(--color--green-90);
    }

    &.warning {
      --Message--color-foreground: var(--color--orange);
      --Message--color-background: var(--color--orange-5);
      --Icon--color: var(--color--orange);
      --Icon--color--hover: var(--color--orange-70);
      --Icon--color--active: var(--color--orange-90);
    }

    &.error {
      --Message--color-foreground: var(--color--red);
      --Message--color-background: var(--color--red-5);
      --Icon--color: var(--color--red);
      --Icon--color--hover: var(--color--red-70);
      --Icon--color--active: var(--color--red-90);
    }
  }

  .message-title {
    font-size: var(--font-size--body-sentence);

    &.size-small {
      font-size: var(--font-size--body-table);
    }
  }

  .message-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing--xs);
  }

  .actions { 
    display: flex;
    align-items: center;
    gap: var(--spacing--xxs);

    --Icon--size: 20px;
  }

  .iconContainer {
    display: flex;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing--xxs);
    margin-top: var(--spacing--xxs);
    padding-top: var(--spacing--xxs);
    border-top: 1px solid var(--Message--color-foreground);

    &.hidden {
      border: 0;
      clip: rect(1px 1px 1px 1px); /* IE6, IE7 */
      height: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
      width: 1px; 
    }
  }

  .arrow-drop-down {
    transform-origin: center;
    transition: transform var(--duration--extra-fast) var(--ease--on-screen);

    &:not(.expanded) {
      transform: rotate(90deg);
    }
  }

</style>
